<template>
  <div class="mb-20 mt-3 bg-white-darker py-4 md:mb-0">
    <div class="container md:max-w-[75rem]">
      <h3
        v-t="'pages.extra_options.title'"
        class="sm:md-3 mb-4 text-2xl font-bold"
      />
      <div class="flex flex-col gap-5 lg:flex-row">
        <div
          class="relative h-[380px] w-full fill-gray-400 text-gray-400 sm:block md:gap-5 lg:max-w-xl"
        >
          <div
            class="absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-2 text-gray-600"
          >
            <span
              class="absolute inset-0 h-full w-full animate-pulse rounded-md bg-black/10"
            />

            <span
              class="absolute inset-0 flex flex-col items-center justify-center gap-2"
            >
              <SpinnerIcon size="w-8 h-8" />
              <span
                v-t="'pages.extra_options.placeholder_text'"
                class="text-xl"
              />
            </span>
          </div>

          <img
            v-if="configuratorStore.mediaclipPreviewUrl"
            :src="configuratorStore.mediaclipPreviewUrl"
            :alt="$t('pages.extra_options.preview_alt')"
            loading="lazy"
            class="absolute h-full w-full rounded-md object-cover opacity-0 transition-opacity duration-300"
            onload="this.classList.remove('opacity-0')"
          />
        </div>
        <div class="w-full">
          <div class="flex h-full flex-col justify-between">
            <div class="flex flex-col justify-between gap-2">
              <PriceOptionEntry
                :name="`${configuratorStore.product.name}: ${sizeStore.selectedSizeValue.label}`"
                :price="sizeStore.selectedSizeValue.attributes.localizedPrice"
                :recommended-price="
                  sizeStore.selectedSizeValue.attributes
                    .localizedRecommendedPrice
                "
              />
              <template
                v-for="(filter, _, index) in filterStore.selectedFilterValues"
                :key="index"
              >
                <PriceOptionEntry
                  :name="`${keyedFilters[index].name}: ${filter.label}`"
                />
              </template>
              <template
                v-for="(
                  option, categoryId
                ) in extraOptionsStore.selectedOptions"
                :key="categoryId"
              >
                <PriceOptionEntry
                  v-if="option"
                  :name="`${option.name ?? option.displayName}`"
                  :price="option.localizedPrice"
                  :recommended-price="option.localizedRecommendedPrice"
                />
              </template>
              <hr class="my-2 border-t border-gray-lighter px-2" />
              <div class="flex justify-between font-bold">
                <span v-t="'general.price_total'" />
                <span>
                  <span
                    v-if="totalRecommendedPrice > sizeStore.totalPrice"
                    class="mr-2 text-gray-light line-through"
                    v-text="formatPrice(totalRecommendedPrice / 100)"
                  />
                  <span v-text="formatPrice(sizeStore.totalPrice / 100)" />
                </span>
              </div>

              <div
                v-if="showTotalSavedPrice"
                class="flex w-full justify-end gap-1 text-sm text-green-darker"
              >
                <span v-t="'pages.extra_options.saved_price'" />
                <span
                  class="font-bold"
                  v-text="formatPrice(totalSavedPrice / 100)"
                />
              </div>
            </div>

            <!--     Sticky buttons for mobile     -->
            <div
              class="fixed bottom-0 left-0 flex w-full justify-between gap-x-4 bg-white shadow-[0_0_3px_rgb(153,153,153)] md:hidden"
            >
              <div class="container my-3 flex h-14 w-full items-center gap-x-4">
                <BackButton
                  :disabled="
                    configuratorStore.isUpdatingProject ||
                    configuratorStore.hasMediaclipError
                  "
                  class="w-1/5"
                  @click="configuratorStore.setConfiguratorStep()"
                />
                <AddToCartButton />
              </div>
            </div>

            <!--     Buttons for medium size screens and up    -->
            <div class="hidden w-full justify-between gap-2 md:flex">
              <BackButton
                :disabled="
                  configuratorStore.isUpdatingProject ||
                  configuratorStore.hasMediaclipError
                "
                always-chevron
                @click="configuratorStore.setConfiguratorStep()"
              />
              <AddToCartButton />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import SpinnerIcon from '@/components/Icons/SpinnerIcon.vue';
import BackButton from '@/components/Buttons/BackButton.vue';
import { usePriceFormatter } from '@/composables/UsePriceFormatter';
import { useConfiguratorStore } from '@/stores/Configurator/ConfiguratorStore';
import { useFilterStore } from '@/stores/Configurator/FilterStore';
import { useSizeStore } from '@/stores/Configurator/SizeStore';
import { useExtraOptionsStore } from '@/stores/ExtraOptionsStore';
import { FilterRecord } from '@/types/Filters/FilterRecord';
import { ProductOptionCategoryProductOptionObject } from '@/types/joostconnector';
import _ from 'lodash';
import { computed, ComputedRef } from 'vue';
import PriceOptionEntry from '../ExtraOptions/PriceOptionEntry.vue';
import AddToCartButton from './AddToCartButton.vue';

const configuratorStore = useConfiguratorStore();
const extraOptionsStore = useExtraOptionsStore();
const filterStore = useFilterStore();
const sizeStore = useSizeStore();
const { formatPrice } = usePriceFormatter();

const keyedFilters: ComputedRef<Record<number, FilterRecord>> = computed(() =>
  _.map(filterStore.filters, 'meta')
);

// Get total recommended price
const totalRecommendedPrice: ComputedRef<number> = computed(() => {
  // Get recommended price of product
  const productRecommendedPrice =
    sizeStore.selectedSizeValue.attributes.recommendedPrice ??
    sizeStore.selectedSizeValue.attributes.price;

  // Get recommended prices of selected options
  const optionRecommendedPrices = _.sumBy(
    _.map(extraOptionsStore.selectedOptions, 'recommendedPrice')
  );

  // Get price of selected options without recommended price
  const optionPrices = _.sumBy(
    _.filter(
      extraOptionsStore.selectedOptions,
      (extraOption: ProductOptionCategoryProductOptionObject) =>
        !extraOption.recommendedPrice
    ),
    'price'
  );

  // Calculate all prices
  return productRecommendedPrice + optionPrices + optionRecommendedPrices;
});

// Get total saved price
const totalSavedPrice: ComputedRef<number> = computed(
  () => totalRecommendedPrice.value - sizeStore.totalPrice
);

const showTotalSavedPrice: ComputedRef<boolean> = computed(
  () =>
    configuratorStore.isUploadConfigurator &&
    totalSavedPrice.value > 0 &&
    totalRecommendedPrice.value > sizeStore.totalPrice
);
</script>
